import _ from "lodash";
import { BOOKING_SYSTEMS_WITH_CUSTOM_BOOKING_TIME_IDS } from "@constants";

export interface PriceGroupLike {
    id: string;
    prices: Array<{ productPricesDetailsId?: string | number | null }>;
}

// Determines which price groups still need their booking-time catalog fetched.
// A group is "pending" when it has not been fetched yet AND it carries at least
// one priced fare with a productPricesDetailsId. The product detail page loads
// progressively (homepage -> FT cache -> TDMS), so an early/partial render may
// not yet expose fares; gating on productPricesDetailsId ensures such a group is
// retried once real fare data arrives instead of being permanently skipped.
export const pendingBookingTimeGroups = <T extends PriceGroupLike>(
    priceGroups: T[],
    fetchedIds: Set<string>,
): T[] =>
    priceGroups.filter(
        (pg) => !fetchedIds.has(pg.id) && pg.prices.some((p) => !_.isNil(p.productPricesDetailsId)),
    );

export interface RawBookingTime {
    BookingTimeID: string | number;
    BookingTime: string;
}

// Builds a de-duplicated departure-time catalog from one or more raw sources
// (e.g. authoritative booking-detail times plus any embedded availability times),
// dropping "Daily availability" placeholders. Earlier sources win on duplicate
// ids so callers can pass the authoritative source first.
export const buildBookingTimeCatalog = (
    ...sources: Array<RawBookingTime[] | undefined | null>
): Array<{ id: string; name: string }> => {
    const seen = new Set<string>();
    const out: Array<{ id: string; name: string }> = [];
    sources.forEach((src) => {
        (src ?? []).forEach((bt) => {
            const id = bt.BookingTimeID.toString();
            if (!seen.has(id) && bt.BookingTime !== "Daily availability") {
                seen.add(id);
                out.push({ id, name: bt.BookingTime });
            }
        });
    });
    return out;
};

export interface DepartureTimeOption {
    id: string;
    displayName: string;
    numAvailable: number;
}

export interface FareDepartureTimesState {
    loading: boolean;
    times: DepartureTimeOption[];
    error?: string;
}

export interface FareRowLike {
    date: string;
    fareGroupId: string;
    productPricesDetailsId: string | number;
    bookingTimeId: string;
    priceGroup: { bookingSystem?: string };
}

export const fareNeedsTimeSelection = (row: FareRowLike): boolean => {
    const bs = row.priceGroup.bookingSystem?.trim().toLowerCase() ?? "";
    return BOOKING_SYSTEMS_WITH_CUSTOM_BOOKING_TIME_IDS.includes(bs);
};

export const fareTimeKey = (row: FareRowLike): string =>
    `${row.date}|${row.fareGroupId}|${row.productPricesDetailsId}`;

export const departureTimeSelectionKey = (
    date: string,
    fareGroupId: string,
    timeId: string,
    availIndex: number,
): string => `${date}|${fareGroupId}|${timeId}|${availIndex}`;

export const parseDepartureTimeFields = (
    fields: Array<{
        name?: string;
        options?: Array<{ externalId: string | number; displayName: string; numAvailable: number }>;
    }>,
): DepartureTimeOption[] => {
    if (!Array.isArray(fields)) return [];
    const timeFields = fields.filter(
        (f) =>
            f &&
            typeof f.name === "string" &&
            f.name.trim().toLowerCase() === "timeid" &&
            Array.isArray(f.options),
    );
    return timeFields.flatMap((f) =>
        (f.options ?? []).map((o) => ({
            id: o.externalId.toString(),
            displayName: o.displayName,
            numAvailable: o.numAvailable,
        })),
    );
};

export interface AvailableTimeRowLike {
    fareGroupId: string;
    productPricesDetailsId: string | number;
    bookingTimeId: string | number;
    numAvailable: number;
    availability: { BookingDate: string };
}

export interface DepartureTimeRowScope {
    fareGroupId: string;
    productPricesDetailsId: string | number;
    bookingDate: string;
}

// Derives a multi-time fare's departure-time options for a single date directly
// from the availability rows already fetched via checkAvailabilityProduct (which
// now returns every booking time + its availability per date), so no extra live
// per-time calls are needed. Rows are first scoped to the clicked fare row — same
// fare group, same productPricesDetailsId and same date — so that a fare group
// holding more than one PPD on a date never borrows another variant's
// availability. Each catalog time is then matched to its scoped row by
// booking-time id; a time with no matching row is reported sold out (0) and gets
// dropped by filterAvailableDepartureTimes.
export const deriveDepartureTimesFromRows = (
    catalog: Array<{ id: string; name: string }>,
    rows: AvailableTimeRowLike[],
    scope: DepartureTimeRowScope,
): DepartureTimeOption[] => {
    const scoped = rows.filter(
        (r) =>
            r.fareGroupId === scope.fareGroupId &&
            String(r.productPricesDetailsId) === String(scope.productPricesDetailsId) &&
            r.availability.BookingDate === scope.bookingDate,
    );
    return catalog.map((bt) => {
        const match = scoped.find((r) => String(r.bookingTimeId) === String(bt.id));
        return {
            id: bt.id,
            displayName: bt.name,
            numAvailable: match?.numAvailable ?? 0,
        };
    });
};

// The calendar only ever offers times the user can actually book: any departure
// time with no remaining availability is hidden entirely (not shown as
// "Sold out"). Times are picked per-date when a date is expanded.
export const filterAvailableDepartureTimes = (
    times: DepartureTimeOption[],
): DepartureTimeOption[] => times.filter((t) => t.numAvailable > 0);

export interface NamedTimeRowLike extends AvailableTimeRowLike {
    bookingTimeName: string;
}

// Derives a fare's departure-time options for a single date directly from the
// availability rows themselves — each row already carries its own booking-time
// id, label and remaining availability. Used when a custom booking system (e.g.
// RespaxV2) returns the times inside the availability rows
// (checkAvailabilityProduct slots with bookingTime + numAvailable) but exposes
// no per-date `timeid` field to parse. Unlike deriveDepartureTimesFromRows there
// is no separate catalog — the rows ARE the catalog. Rows are scoped to the
// clicked fare (same fare group, same productPricesDetailsId, same date) so a
// fare group with multiple PPDs on a date never borrows another variant's times.
// Placeholder/blank times are skipped and duplicate ids de-duped (first wins).
export const deriveDepartureTimesFromAvailabilityRows = (
    rows: NamedTimeRowLike[],
    scope: DepartureTimeRowScope,
): DepartureTimeOption[] => {
    const seen = new Set<string>();
    const out: DepartureTimeOption[] = [];
    rows.forEach((r) => {
        if (
            r.fareGroupId !== scope.fareGroupId ||
            String(r.productPricesDetailsId) !== String(scope.productPricesDetailsId) ||
            r.availability.BookingDate !== scope.bookingDate
        ) {
            return;
        }
        if (_.isNil(r.bookingTimeId)) return;
        const id = String(r.bookingTimeId);
        const name = r.bookingTimeName ?? "";
        if (id === "" || name === "" || name === "Daily availability") return;
        if (seen.has(id)) return;
        seen.add(id);
        out.push({ id, displayName: name, numAvailable: r.numAvailable });
    });
    return out;
};

export interface CollapsibleFareRow {
    fareGroupId: string;
    productPricesDetailsId: string | number;
}

// A multi-time fare arrives as one availability row per booking time (the
// product availability endpoint returns every time + its availability per date),
// so a single fare would otherwise render once per time — e.g. ADULT appearing 6
// times. For any fare that uses the per-date departure-time accordion
// (`needsTime`), collapse its rows down to a single representative card; the
// individual times are surfaced inside the accordion instead. Identity is
// fareGroupId + productPricesDetailsId (NOT fareGroupId alone): the same fare
// group can have multiple priced variants on one date, each with its own PPD and
// its own time accordion (`fareTimeKey` is scoped by PPD), so collapsing by
// fareGroupId alone would hide and make a distinct variant unselectable. Fares
// that don't need time selection are left untouched (each is a distinct bookable
// row). Input order is preserved, so the representative is the first (earliest)
// row for that variant.
export const collapseTimedFareRows = <T extends CollapsibleFareRow>(
    rows: T[],
    needsTime: (row: T) => boolean,
): T[] => {
    const seenTimedVariants = new Set<string>();
    const out: T[] = [];
    for (const row of rows) {
        if (needsTime(row)) {
            const key = `${row.fareGroupId}|${row.productPricesDetailsId}`;
            if (seenTimedVariants.has(key)) continue;
            seenTimedVariants.add(key);
        }
        out.push(row);
    }
    return out;
};

export interface MergeableFareRow extends CollapsibleFareRow {
    date: string;
    bookingTimeId: string;
}

/**
 * Fold a freshly fetched batch of availability rows into the ones already held.
 *
 * Rows accumulate across fetches, and the months-already-fetched guard is reset
 * whenever the panel re-runs its auto-fetch — in development React mounts effects
 * twice, so the same three months are fetched again and every fare on every date
 * appears a second time. The duplicates are identical, so the selection key
 * matches both and ticking one fare ticks its twin.
 *
 * Identity is date + fare group + priced variant + booking time. That is the same
 * granularity the rest of the panel uses to find a row, so anything genuinely
 * distinct is kept: the same fare group can have several PPDs on one date, and a
 * multi-time fare has one row per time.
 *
 * The incoming row wins, being the fresher read of availability.
 */
export const mergeDisplayRows = <T extends MergeableFareRow>(previous: T[], incoming: T[]): T[] => {
    const rowKey = (row: T) =>
        `${row.date}|${row.fareGroupId}|${row.productPricesDetailsId}|${row.bookingTimeId}`;
    const merged = new Map<string, T>();
    for (const row of previous) merged.set(rowKey(row), row);
    for (const row of incoming) merged.set(rowKey(row), row);
    return Array.from(merged.values());
};

export interface BookingTimeCatalogSource {
    priceGroup: { id: string };
    bookingTimes: Array<{ id: string; name: string }>;
}

// The per-fare booking-time catalog is bootstrapped from the product's
// CONFIGURED price groups, but availability results can introduce
// response-derived (orphan) fare groups whose ids are their response PPD —
// e.g. a new season's ADULT/CHILD fares whose PPDs no configured group owns.
// Those groups never make it into the bootstrap catalog, so a multi-time fare
// renders once per departure time (ADULT 07:00, ADULT 09:45, ...) instead of
// collapsing into one card with the departure-time accordion. Merge each
// availability result's own booking times into the catalog for any group that
// doesn't already have a (non-empty) catalog entry; existing entries from the
// bootstrap fetch are never overwritten.
export const mergeFareGroupBookingTimes = (
    prev: Record<string, Array<{ id: string; name: string }>>,
    results: BookingTimeCatalogSource[],
): Record<string, Array<{ id: string; name: string }>> => {
    let changed = false;
    const merged = { ...prev };
    results.forEach(({ priceGroup, bookingTimes }) => {
        if ((merged[priceGroup.id]?.length ?? 0) === 0 && bookingTimes.length > 0) {
            merged[priceGroup.id] = bookingTimes;
            changed = true;
        }
    });
    return changed ? merged : prev;
};

export interface TimeAvailRowLike {
    fareGroupId: string;
    bookingTimeId: string | number;
    availIndex: number;
    availability: { BookingDate: string };
}

// When a card is collapsed to one representative row but the user then picks a
// specific departure time, the representative's `availIndex` belongs to a
// different time. Resolve the row that actually matches the chosen
// fare-group/date/time so the correct availability index is booked (a wrong
// index sends the wrong slot to the cart / triggers a 400 validation error).
// Returns undefined when no matching row exists (e.g. IBIS/RespaxV2, where the
// time is a sub-field of a single date row) so callers can fall back to the
// representative row.
export const findTimeAvailRow = <T extends TimeAvailRowLike>(
    rows: T[],
    scope: { fareGroupId: string; bookingDate: string; timeId: string },
): T | undefined =>
    rows.find(
        (r) =>
            r.fareGroupId === scope.fareGroupId &&
            r.availability.BookingDate === scope.bookingDate &&
            String(r.bookingTimeId) === String(scope.timeId),
    );
